import { shallowRef, type ShallowRef } from 'vue';
import type { LocaleData, Part } from '../message/ast';
import { formatParts } from '../message/format';
import { matchLocale } from './locale';
import { activeLocale } from './state';

/** What a compiled Translation file passes to `defineTranslations`: every locale inline, Messages parsed. */
export interface TranslationsDefinition {
    readonly defaultLocale: string;
    readonly locales: Readonly<Record<string, LocaleData>>;
}

const lookup = (data: LocaleData | undefined, path: readonly string[]): Part[] | undefined => {
    let node: Part[] | LocaleData | undefined = data;
    for (const key of path) {
        if (node === undefined || Array.isArray(node)) return undefined;
        node = Object.hasOwn(node, key) ? node[key] : undefined;
    }
    return Array.isArray(node) ? node : undefined;
};

/**
 * The Messages to read and the locale to format them for. A regional Active locale reads its language
 * (`nl-NL` → `nl`) and formats for its region. A locale whose language the file lacks reads and formats
 * the Default locale. A Message missing from the Active locale's language falls back to the Default locale's
 * text, still formatted for the Active locale, silently.
 */
function resolve(current: ShallowRef<TranslationsDefinition>, path: readonly string[]): { parts: Part[]; locale: string } {
    const definition = current.value;
    const active = activeLocale.value;
    const language = active === undefined ? undefined : matchLocale(active, Object.keys(definition.locales));
    if (active !== undefined && language !== undefined) {
        const parts = lookup(definition.locales[language], path);
        if (parts) return { parts, locale: active };
    }
    const parts = lookup(definition.locales[definition.defaultLocale], path)!;
    return { parts, locale: language === undefined ? definition.defaultLocale : active! };
}

const hasParams = (parts: Part[]) => parts.some(part => typeof part !== 'string');

/** The keys of the Default locale, and which Messages are functions: what a hot update may not change. */
const shapeOf = (data: LocaleData): unknown =>
    Object.fromEntries(Object.entries(data).map(([key, child]) => [key, Array.isArray(child) ? hasParams(child) : shapeOf(child)]));
const defaultShape = (definition: TranslationsDefinition) => JSON.stringify(shapeOf(definition.locales[definition.defaultLocale] ?? {}));

/** The definition behind each object `defineTranslations` returned, swapped by a hot update. */
const sources = new WeakMap<object, { current: ShallowRef<TranslationsDefinition>; shape: string }>();

/**
 * The live Messages of a Translation file, shaped like its Default locale: a getter per plain Message,
 * one stable function per Message with parameters (it reads the Active locale when called), a frozen object
 * per group. Reading through it inside a render or `computed` tracks the Active locale.
 *
 * A hot update passes the object the file's previous version returned as `previous`. With the same keys,
 * and the same Messages taking parameters, the new Messages replace the old ones in place: `previous` is
 * returned, importers keep their binding, and whatever read it re-renders. Otherwise a new object is returned
 * and `previous` is left alone (the dev server reloads the page for such a change).
 *
 * What the code generated for a Translation file calls; not for hand-written code.
 */
export function defineTranslations<T extends object>(definition: TranslationsDefinition, previous?: object): T {
    const shape = defaultShape(definition);
    const source = previous && sources.get(previous);
    if (source?.shape === shape) {
        source.current.value = definition;
        return previous as T;
    }
    const current = shallowRef(definition);
    const build = (shape: LocaleData, path: readonly string[]): object => {
        const view: Record<string, unknown> = {};
        for (const [key, child] of Object.entries(shape)) {
            const at = [...path, key];
            if (!Array.isArray(child)) {
                Object.defineProperty(view, key, { enumerable: true, value: build(child, at) });
            } else if (hasParams(child)) {
                const message = (params: Record<string, unknown>) => {
                    const { parts, locale } = resolve(current, at);
                    return formatParts(parts, locale, params);
                };
                Object.defineProperty(view, key, { enumerable: true, value: message });
            } else {
                Object.defineProperty(view, key, {
                    enumerable: true,
                    get: () => {
                        const { parts, locale } = resolve(current, at);
                        return formatParts(parts, locale);
                    },
                });
            }
        }
        return Object.freeze(view);
    };
    const view = build(definition.locales[definition.defaultLocale] ?? {}, []);
    sources.set(view, { current, shape });
    return view as T;
}
